import React, { useRef, useState } from 'react';
import ReactCodeInput from 'react-verification-code-input';
import { smsCodeStep as smsCodeLocales } from 'globals/locales/registration';
import { Box } from '@material-ui/core';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsNextArrowIcon from 'ui-elements/icons/FsNextArrow';
import { wait } from 'globals/utils';
import { useSnackbar } from 'notistack';
import defaultLocales from 'globals/locales/default';
import FsLink from 'ui-elements/links/FsLink';
import {
  ErrorLabel,
  LinksWrapperBox,
  SmsCodeWrapperBox,
} from './FsSmsCodeForm.style-elements';
import { useStyles } from './FsSmsCodeForm.style';

interface FsSmsCodeFormProps {
  onGoPrev?: () => void;
  onGoNext?: () => void;
  withResend?: boolean;
  withChangeNumber?: boolean;
}

const FsSmsCodeForm: React.FC<FsSmsCodeFormProps> = ({
  onGoPrev,
  onGoNext,
  withResend = true,
  withChangeNumber = true,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { smsField: smsFieldClass } = useStyles();
  const formEl = useRef<HTMLFormElement>(null);
  const [pending, setPending] = useState<boolean>(false);
  const [smsCode, setSmsCode] = useState<string>();
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const isCodeValid = smsCode?.length === 4;

  const handleComplete = (code: string) => {
    setSmsCode(code);
  };

  const handleChange = (code: string) => {
    setSmsCode(code);
    setShowAlert(false);
  };

  const handleChangePhoneNumber = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (onGoPrev) onGoPrev();
  };

  const handleResend = (event: React.SyntheticEvent) => {
    event.preventDefault();
    enqueueSnackbar(smsCodeLocales.notifications.sendAgain, {
      variant: 'success',
    });
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setPending(true);
    // eslint-disable-next-line no-console
    console.log('Save changes', {
      smsCode,
    });
    await wait(1000);
    setPending(false);
    if (smsCode === '1234') {
      enqueueSnackbar(smsCodeLocales.notifications.successSmsCode, {
        variant: 'success',
      });
      if (onGoNext) onGoNext();
      return;
    }
    enqueueSnackbar(smsCodeLocales.notifications.errorSmsCode, {
      variant: 'error',
    });
    setShowAlert(true);
  };

  return (
    <Box>
      {showAlert && (
        <ErrorLabel>{smsCodeLocales.wrongSmsCodeErrorLabel}</ErrorLabel>
      )}
      <form onSubmit={handleSubmit} ref={formEl}>
        <SmsCodeWrapperBox>
          <ReactCodeInput
            fields={4}
            fieldHeight={72}
            fieldWidth={62}
            className={smsFieldClass}
            onComplete={handleComplete}
            onChange={handleChange}
            autoFocus
          />
        </SmsCodeWrapperBox>

        <LinksWrapperBox>
          {withResend && (
            <Box marginBottom={2}>
              <FsLink to="#" onClick={handleResend}>
                {defaultLocales.resendCode}
              </FsLink>
            </Box>
          )}
          {withChangeNumber && (
            <Box>
              <FsLink to="#" onClick={handleChangePhoneNumber}>
                {defaultLocales.changePhoneNumber}
              </FsLink>
            </Box>
          )}
        </LinksWrapperBox>

        <FsPendingButton
          type="submit"
          variant="contained"
          color="primary"
          pending={pending}
          disabled={!isCodeValid}
          endIcon={<FsNextArrowIcon />}
          fullWidth
          spaceBetween
        >
          {defaultLocales.verify}
        </FsPendingButton>
      </form>
    </Box>
  );
};

export default FsSmsCodeForm;
